<template>
    <div class="flex flex-col border-2 rounded-lgr px-5 py-7.5 w-full">
        <div class="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-4 pb-5 border-b-2 w-full">
            <div class="title-body text-ink capitalize wrap-break-word min-w-0 sm:flex-1">{{ feedback.question }}</div>
            <div class="flex flex-wrap gap-1.25 justify-start sm:justify-end items-center">
                <template v-if="!feedback.report_cs_id">
                    <BaseTooltip side="bottom" trigger-aria-label="copy url tooltip trigger">
                        <template #trigger>
                            <BaseButton icon-only theme="neutral" @click="handleCopyUrl">
                                <i :class="['text-base/none', urlCopied ? 'bi bi-check-lg text-green-600' : 'bi bi-link-45deg text-valence-primary-500']" aria-hidden="true"></i>
                            </BaseButton>
                        </template>
                        <template #content>
                            {{ urlButtonText }}
                        </template>
                    </BaseTooltip>
                    <BaseTooltip side="bottom" trigger-aria-label="copy link tooltip trigger">
                        <template #trigger>
                            <BaseButton icon-only theme="neutral" @click="handleCopyLink">
                                <i :class="['text-base/none', linkCopied ? 'bi bi-check-lg text-green-600' : 'bi bi-clipboard text-valence-primary-500']" aria-hidden="true"></i>
                            </BaseButton>
                        </template>
                        <template #content>
                            {{ buttonText }}
                        </template>
                    </BaseTooltip>
                    <BaseTooltip side="bottom" trigger-aria-label="close and review tooltip trigger">
                        <template #trigger>
                            <BaseButton theme="neutral" :disabled="isReviewDisabled" class="min-w-40" @click="$emit('close-and-review', feedback)">
                                <div class="whitespace-nowrap">Close & Review</div>
                            </BaseButton>
                        </template>
                        <template v-if="isReviewDisabled" #content> Collect {{ minimumResponseCount }} responses to close </template>
                    </BaseTooltip>
                </template>
                <template v-else>
                    <BaseButton theme="secondary" class="min-w-40" @click="$emit('view-report', feedback)">
                        <div class="whitespace-nowrap">View Report</div>
                    </BaseButton>
                    <BaseTooltip side="bottom" trigger-aria-label="reopen feedback tooltip trigger">
                        <template #trigger>
                            <BaseButton theme="neutral" @click="$emit('reopen', feedback)">
                                <div class="whitespace-nowrap">Reopen</div>
                            </BaseButton>
                        </template>
                        <template #content>Accept new responses on this question again</template>
                    </BaseTooltip>
                </template>
                <BaseTooltip side="bottom" trigger-aria-label="delete feedback tooltip trigger">
                    <template #trigger>
                        <BaseButton icon-only theme="tertiary" @click="$emit('delete', feedback.id)">
                            <i class="bi bi-trash3 text-valence-grey-750 leading-none" aria-hidden="true"></i>
                        </BaseButton>
                    </template>
                    <template #content> Delete </template>
                </BaseTooltip>
            </div>
        </div>
        <div class="flex flex-wrap gap-x-7.5 gap-y-1 pt-5">
            <div class="body text-valence-grey-750">{{ feedback.created_at }}</div>
            <div class="body text-valence-grey-750">{{ feedback.response_count }} responses</div>
        </div>
    </div>
</template>

<script setup>
import BaseButton from "~vue/components/BaseButton.vue";
import BaseTooltip from "~vue/components/BaseTooltip.vue";
import { useTimedShow } from "~vue/composables/useTimedShow";
import { computed } from "vue";

const props = defineProps({
    feedback: {
        type: Object,
        required: true,
    },
    buttonText: {
        type: String,
        default: "Copy Invite",
    },
    urlButtonText: {
        type: String,
        default: "Copy URL",
    },
    minimumResponseCount: {
        type: Number,
        required: true,
    },
});

const isReviewDisabled = computed(() => props.feedback.response_count < props.minimumResponseCount);

const emit = defineEmits(["delete", "copy-link", "copy-url", "close-and-review", "view-report", "reopen"]);

// Local tap-feedback state — gives users a visible confirmation on mobile where
// :hover and tooltips are unreliable. Per-card so neighboring cards don't flash.
const { isShowing: linkCopied, show: showLinkCopied } = useTimedShow({ duration: 1500 });
const { isShowing: urlCopied, show: showUrlCopied } = useTimedShow({ duration: 1500 });

const handleCopyLink = () => {
    emit("copy-link", props.feedback.id);
    showLinkCopied();
};

const handleCopyUrl = () => {
    emit("copy-url", props.feedback.id);
    showUrlCopied();
};
</script>
